/* eslint-disable @typescript-eslint/no-unused-vars */
import api from '../../../api';
import {
  LinkedInIntegration,
  LinkedInCampaign,
  LinkedInAccount,
  LinkedInStepConfig,
  LinkedInPushLog,
  PaginationMeta,
  FailedTaskDetail,
  VerifyIntegrationResponse,
} from '../types';
import { LinkedInPlatform } from '../enums';

// ─── Integration ─────────────────────────────────────────────────────────────

export const getLinkedInIntegrations = async (): Promise<LinkedInIntegration[]> => {
  const response = await api.get('/linkedin-automation/integrations');
  return response.payload;
};

export const connectLinkedInIntegration = async (payload: {
  platform: LinkedInPlatform;
  apiKey: string;
}): Promise<LinkedInIntegration> => {
  const response = await api.post('/linkedin-automation/integrations', payload);
  return response.payload;
};

export const rotateApiKey = async (
  id: number,
  payload: { apiKey: string },
): Promise<LinkedInIntegration> => {
  const response = await api.patch(`/linkedin-automation/integrations/${id}`, payload);
  return response.payload;
};

export const updateFieldMappings = async (
  id: number,
  payload: { fieldMappings: Array<{ platformField: string; shFieldId: number | null }> },
): Promise<LinkedInIntegration> => {
  const response = await api.patch(`/linkedin-automation/integrations/${id}`, payload);
  return response.payload;
};

export const deleteLinkedInIntegration = async (id: number): Promise<void> => {
  await api.delete(`/linkedin-automation/integrations/${id}`);
};

export const verifyLinkedInIntegration = async (id: number): Promise<VerifyIntegrationResponse> => {
  const response = await api.post(`/linkedin-automation/integrations/${id}/verify`);
  return response.payload;
};

export const reconnectLinkedInIntegration = async (
  id: number,
  apiKey?: string,
): Promise<LinkedInIntegration> => {
  const response = await api.post(
    `/linkedin-automation/integrations/${id}/reconnect`,
    apiKey ? { apiKey } : undefined,
  );
  return response.payload;
};

// ─── Campaigns ───────────────────────────────────────────────────────────────

export const getLinkedInCampaigns = async (
  integrationId: number,
  refresh?: boolean,
): Promise<{ campaigns: LinkedInCampaign[]; cachedAt: string }> => {
  const params = refresh ? '?refresh=true' : '';
  const response = await api.get(
    `/linkedin-automation/integrations/${integrationId}/campaigns${params}`,
  );
  return response.payload;
};

export const getLinkedInAccounts = async (
  integrationId: number,
): Promise<LinkedInAccount[]> => {
  const response = await api.get(
    `/linkedin-automation/integrations/${integrationId}/linkedin-accounts`,
  );
  return response.payload;
};

// ─── Step Config (STUB — API endpoint not yet documented) ────────────────────

export const getStepConfig = async (stepId: number): Promise<LinkedInStepConfig> => 
  // TODO: Replace with real API call once backend endpoint is documented
  // Expected: GET /sequences/:sequenceId/steps/:stepId/linkedin-config
   ({
    id: 1,
    stepId,
    sequenceId: 100,
    linkedinIntegrationId: 42,
    platform: LinkedInPlatform.Aimfox,
    externalCampaignId: 'campaign_abc',
    campaignName: 'Q1 Outreach 2026',
    heyreachLinkedinAccountId: null,
  })
;

// ─── Tasks (STUB — API endpoints not yet documented) ─────────────────────────

export const retryLinkedInTasks = async (
  taskIds: number[],
  fieldUpdates?: Record<string, string>,
): Promise<{ retried: number[]; skipped: number[] }> =>
  // TODO: Replace with real API call once backend endpoint is documented
  // Expected: POST /linkedin-automation/tasks/retry { taskIds, fieldUpdates? }
   ({ retried: taskIds, skipped: [] })
;

export const getFailedLinkedInTasks = async (_filters: {
  page?: number;
  limit?: number;
}): Promise<{ tasks: FailedTaskDetail[]; total: number }> => 
  // TODO: Replace with real API call once backend endpoint is documented
  // Expected: GET /linkedin-automation/tasks/failed?page=1&limit=20
   ({ tasks: [], total: 0 })
;

// ─── Push Logs (STUB — API endpoint not yet documented) ──────────────────────

export const getPushLogs = async (_filters: {
  taskId?: number;
  page?: number;
  limit?: number;
}): Promise<{ data: LinkedInPushLog[]; meta: PaginationMeta }> => 
  // TODO: Replace with real API call once backend endpoint is documented
  // Expected: GET /linkedin-automation/push-logs?taskId=123&page=1&limit=20
   ({ data: [], meta: { total: 0, page: 1, limit: 20 } })
;

// ─── Tour Preference (localStorage — no API needed) ──────────────────────────

const TOUR_SEEN_KEY = 'li_automation_tour_seen';

export const getLinkedInAutomationTourSeen = async (): Promise<{ seen: boolean }> => {
  const seen = localStorage.getItem(TOUR_SEEN_KEY) === 'true';
  return { seen };
};

export const dismissLinkedInAutomationTour = async (): Promise<{ success: boolean }> => {
  localStorage.setItem(TOUR_SEEN_KEY, 'true');
  return { success: true };
};
